import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { ORDER_STATUSES, LINE_STATUSES, sortSizes } from '../firebase/schema';
import { formatCurrency } from '../utils/formatters';
import { formatDate } from '../utils/dateFormatter';
import { getMockupUrl } from '../utils/fileHelpers';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

const TRACKING_STEPS = [
  { label: 'Order placed', key: 'placed' },
  { label: 'Shirts ordered', key: 'ordered' },
  { label: 'Shirts arrived', key: 'arrived' },
  { label: 'Scheduled to print', key: 'production' },
  { label: 'Ready for pickup', key: 'pickup' }
];

/** Map order-level status to tracker step (used when we don't have line data). */
function getStepIndexFromOrder(order) {
  if (!order || !order.status) return 0;
  const s = order.status;
  if (s === ORDER_STATUSES.CANCELLED) return -1;
  if (s === ORDER_STATUSES.CREATED || s === ORDER_STATUSES.IN_PROGRESS || s === ORDER_STATUSES.READY_TO_ORDER) return 0;
  if (s === ORDER_STATUSES.ORDERED) return 1;
  if (s === ORDER_STATUSES.RECEIVED || s === ORDER_STATUSES.PARTIALLY_RECEIVED) return 2;
  if (s === ORDER_STATUSES.WAITING_ON_REPRINT) return 2;
  if (s === ORDER_STATUSES.PARTIALLY_READY_TO_PRINT || s === ORDER_STATUSES.READY_TO_PRINT) return 3;
  if (s === ORDER_STATUSES.READY_TO_INVOICE || s === ORDER_STATUSES.COMPLETED) return 4;
  return 2;
}

/** Map a single line's status to the furthest tracker step that line has reached. */
function lineStatusToStep(lineStatus) {
  if (!lineStatus) return 0;
  const s = String(lineStatus);
  if (s === LINE_STATUSES.NEW || s === LINE_STATUSES.READY_TO_ORDER) return 0;
  if (s === LINE_STATUSES.ORDERED) return 1;
  if (s === LINE_STATUSES.IN_NOT_CUT || s === LINE_STATUSES.CUT_NOT_IN) return 2; // shirts arrived
  if (s === LINE_STATUSES.READY_TO_PRINT) return 3; // scheduled to print
  if (s === LINE_STATUSES.PRINTED || s === LINE_STATUSES.COMPLETED) return 4;
  return 0;
}

/** Tracker step from line statuses when available; otherwise from order status. */
function getStepIndex(order, lines) {
  if (order?.status === ORDER_STATUSES.CANCELLED) return -1;
  const orderOnlyStep = getStepIndexFromOrder(order);
  if (!lines || lines.length === 0) return orderOnlyStep;
  const lineSteps = lines.map((l) => lineStatusToStep(l.status));
  const maxLineStep = Math.max(0, ...lineSteps);
  const allLinesDone = lines.every(
    (l) =>
      l.status === LINE_STATUSES.PRINTED || l.status === LINE_STATUSES.COMPLETED
  );
  const orderDone =
    order?.status === ORDER_STATUSES.READY_TO_INVOICE ||
    order?.status === ORDER_STATUSES.COMPLETED;
  if (allLinesDone || orderDone) return 4;
  return maxLineStep;
}

/** Compute subtotal and total from line items using the same formula as OrderView: lines-only subtotal, then discount, then 7% tax on (subtotal - discount). */
function computeTotalsFromLines(order, lines) {
  if (!lines || lines.length === 0) return null;
  const subtotal = lines.reduce((sum, line) => {
    const lineQty = line.totalQuantity != null
      ? Number(line.totalQuantity)
      : (line.sizes && typeof line.sizes === 'object'
        ? Object.values(line.sizes).reduce((s, qty) => s + (Number(qty) || 0), 0)
        : 0);
    const price = line.pricePerPiece != null ? Number(line.pricePerPiece) : Number(line.price) || 0;
    return sum + lineQty * price;
  }, 0);
  let discountAmount = 0;
  if (order?.discount && order.discount.amount != null && order.discount.amount !== '') {
    if (order.discount.type === 'percent') {
      discountAmount = subtotal * (parseFloat(order.discount.amount) / 100);
    } else {
      discountAmount = parseFloat(order.discount.amount) || 0;
    }
  }
  const taxableAmount = subtotal - discountAmount;
  const taxExempt = order?.taxExempt === true;
  const tax = taxExempt ? 0 : taxableAmount * 0.07;
  const total = subtotal - discountAmount + tax;
  return { subtotal, total };
}

/* Match Eight Seven Central public site: white/light bg, black text, minimal & sharp */
const Page = styled.div`
  min-height: 100vh;
  background: #ffffff;
  color: #111111;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
  padding: 2rem 1rem 3rem;
  box-sizing: border-box;
`;

const Container = styled.div`
  max-width: 560px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #111111;
  margin: 0 0 0.5rem;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 0.85rem;
  color: rgba(0,0,0,0.55);
  text-align: center;
  margin: 0 0 2rem;
`;

const Card = styled.div`
  background: #fafafa;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const OrderId = styled.div`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(0,0,0,0.5);
  margin-bottom: 0.25rem;
`;
const OrderValue = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111111;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.75rem;
  &:last-child { margin-bottom: 0; }
`;
const Label = styled.span`
  font-size: 0.8rem;
  color: rgba(0,0,0,0.55);
`;
const Value = styled.span`
  font-size: 0.95rem;
  font-weight: 500;
  color: #111111;
`;

const ProgressWrap = styled.div`
  margin: 2rem 0;
`;
const ProgressBar = styled.div`
  height: 6px;
  background: #e5e5e5;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 1.25rem;
`;
const ProgressFill = styled.div`
  height: 100%;
  width: ${p => p.percent}%;
  background: #111111;
  border-radius: 3px;
  transition: width 0.5s ease;
`;
const StepsList = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  padding: 0 0.25rem;
`;
const Step = styled.div`
  flex: 0 0 auto;
  width: 20%;
  text-align: center;
  position: relative;
  z-index: 1;
`;
const dotPulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.75; transform: scale(1.12); }
`;
const labelPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const StepDot = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  margin: 0 auto 0.5rem;
  background: ${p => p.completed ? '#111111' : p.active ? 'rgba(17,17,17,0.5)' : '#e5e5e5'};
  border: 2px solid ${p => p.completed || p.active ? '#111111' : '#e0e0e0'};
  transition: background 0.3s, border-color 0.3s;
  ${p => p.active && `animation: ${dotPulse} 2s ease-in-out infinite;`}
`;
const StepLabel = styled.div`
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${p => p.completed || p.active ? '#111111' : 'rgba(0,0,0,0.4)'};
  line-height: 1.3;
  ${p => p.active && `animation: ${labelPulse} 2s ease-in-out infinite;`}
`;

const CancelledBanner = styled.div`
  background: #fff5f5;
  border: 1px solid #ffcdd2;
  color: #c62828;
  padding: 1rem 1.25rem;
  border-radius: 4px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const Loading = styled.div`
  text-align: center;
  padding: 4rem 1rem;
  color: rgba(0,0,0,0.55);
`;
const NotFound = styled.div`
  text-align: center;
  padding: 3rem 1rem;
`;
const NotFoundTitle = styled.h2`
  font-size: 1.25rem;
  color: #111111;
  margin: 0 0 0.5rem;
`;
const NotFoundText = styled.p`
  font-size: 0.9rem;
  color: rgba(0,0,0,0.55);
  margin: 0;
`;

const SectionTitle = styled.h2`
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(0,0,0,0.5);
  margin: 0 0 1rem;
`;

const LineItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e5e5e5;
  &:last-child { border-bottom: none; }
`;

const LineMockup = styled.div`
  flex-shrink: 0;
  width: 72px;
  height: 72px;
  background: #e5e5e5;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const LineMockupImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
const LineInfo = styled.div`
  flex: 1;
  min-width: 0;
`;
const LineDesignName = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #111111;
  margin-bottom: 0.25rem;
`;
const LineMeta = styled.div`
  font-size: 0.75rem;
  color: rgba(0,0,0,0.55);
`;
const LineRight = styled.div`
  flex-shrink: 0;
  text-align: right;
`;
const LineSizeBreakdown = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #111111;
  margin-bottom: 0.2rem;
`;
const LinePrice = styled.div`
  font-size: 0.75rem;
  color: rgba(0,0,0,0.55);
`;

const OrderTracker = () => {
  const { slug } = useParams();
  const [order, setOrder] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      setError('missing');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const orderId = slug.trim();
        const orderSnap = await getDoc(doc(db, 'orders', orderId));
        if (cancelled) return;
        if (!orderSnap.exists()) {
          setError('notfound');
          setOrder(null);
          setLines([]);
        } else {
          const orderData = { id: orderSnap.id, ...orderSnap.data() };
          setOrder(orderData);
          setError(null);
          const linesQ = query(
            collection(db, 'orderLines'),
            where('orderId', '==', orderSnap.id)
          );
          const linesSnap = await getDocs(linesQ);
          if (!cancelled) {
            const lineList = linesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            lineList.sort((a, b) => (a.lineNumber || 0) - (b.lineNumber || 0));
            setLines(lineList);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('OrderTracker fetch error:', err);
          setError('notfound');
          setOrder(null);
          setLines([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <Page>
        <Container>
          <Title>Order tracker</Title>
          <Subtitle>Loading…</Subtitle>
          <Loading>Finding your order…</Loading>
        </Container>
      </Page>
    );
  }

  if (error || !order) {
    return (
      <Page>
        <Container>
          <Title>Order tracker</Title>
          <Subtitle>Track your order</Subtitle>
          <NotFound>
            <NotFoundTitle>Order not found</NotFoundTitle>
            <NotFoundText>
              We couldn’t find an order with this link. Please check the URL or contact us.
            </NotFoundText>
          </NotFound>
        </Container>
      </Page>
    );
  }

  const stepIndex = getStepIndex(order, lines);
  const isCancelled = order.status === ORDER_STATUSES.CANCELLED;
  const progressPercent = isCancelled ? 0 : (stepIndex >= 0 ? (stepIndex / (TRACKING_STEPS.length - 1)) * 100 : 0);

  const dueDate = order.dueDate;

  return (
    <Page>
      <Container>
        <Title>Order tracker</Title>
        <Subtitle>Your order status</Subtitle>

        {/* Tracker at top */}
        {isCancelled && (
          <CancelledBanner>This order has been cancelled.</CancelledBanner>
        )}

        {!isCancelled && (
          <Card>
            <ProgressWrap>
              <ProgressBar>
                <ProgressFill percent={progressPercent} />
              </ProgressBar>
              <StepsList>
                {TRACKING_STEPS.map((step, i) => {
                  const completed = stepIndex > i;
                  const active = stepIndex === i;
                  return (
                    <Step key={step.key}>
                      <StepDot completed={completed} active={active} />
                      <StepLabel completed={completed} active={active}>{step.label}</StepLabel>
                    </Step>
                  );
                })}
              </StepsList>
            </ProgressWrap>
          </Card>
        )}

        {/* Order summary */}
        <Card>
          <OrderId>Invoice #</OrderId>
          <OrderValue>{order.poNumber || order.orderNumber || order.id}</OrderValue>
          <Row>
            <Label>Customer</Label>
            <Value>{order.customerName || '—'}</Value>
          </Row>
          {dueDate != null && (
            <Row>
              <Label>Due date</Label>
              <Value>{formatDate(dueDate)}</Value>
            </Row>
          )}
          {(() => {
            const totalPieces = order.totalQuantity != null ? order.totalQuantity : lines.reduce((sum, l) => sum + (l.totalQuantity || 0), 0);
            const computed = computeTotalsFromLines(order, lines);
            const subtotal = computed ? computed.subtotal : (order.subtotal != null ? order.subtotal : null);
            const orderTotal = computed ? computed.total : (order.orderTotal != null ? order.orderTotal : order.subtotal);
            return (
              <>
                <Row>
                  <Label>Total pieces</Label>
                  <Value>{totalPieces}</Value>
                </Row>
                {subtotal != null && (
                  <Row>
                    <Label>Subtotal</Label>
                    <Value>{formatCurrency(subtotal)}</Value>
                  </Row>
                )}
                {orderTotal != null && (
                  <Row>
                    <Label>Total (after tax)</Label>
                    <Value>{formatCurrency(orderTotal)}</Value>
                  </Row>
                )}
              </>
            );
          })()}
        </Card>

        {/* Line items: mockup + quantities */}
        {lines.length > 0 && (
          <Card>
            <SectionTitle>Order details</SectionTitle>
            {lines.map((line) => {
              const mockupUrl = getMockupUrl(line);
              const sizes = line.sizes && typeof line.sizes === 'object' ? line.sizes : {};
              const sizeEntries = Object.entries(sizes)
                .filter(([, v]) => Number(v) > 0)
                .sort(([a], [b]) => sortSizes(a, b));
              const sizeBreakdown = sizeEntries.length > 0
                ? sizeEntries.map(([size, q]) => `${q} ${size}`).join('  ')
                : null;
              const totalQty = line.totalQuantity != null ? line.totalQuantity : sizeEntries.reduce((sum, [, q]) => sum + Number(q), 0);
              const pricePerPiece = line.pricePerPiece != null ? line.pricePerPiece : line.price;
              return (
                <LineItemRow key={line.id}>
                  <LineMockup>
                    {mockupUrl ? (
                      <LineMockupImg src={mockupUrl} alt={line.designName || 'Design'} />
                    ) : (
                      <span style={{ fontSize: '1.5rem', color: '#999' }}>?</span>
                    )}
                  </LineMockup>
                  <LineInfo>
                    <LineDesignName>{line.designName || 'Unnamed design'}</LineDesignName>
                    <LineMeta>
                      {line.garmentColor && `${line.garmentColor}`}
                      {line.garmentId && line.garmentColor ? ` · ${line.garmentId}` : line.garmentId || ''}
                    </LineMeta>
                  </LineInfo>
                  <LineRight>
                    <LineSizeBreakdown>
                      {sizeBreakdown || `${totalQty} ${totalQty === 1 ? 'pc' : 'pcs'}`}
                    </LineSizeBreakdown>
                    {pricePerPiece != null && (
                      <LinePrice>{formatCurrency(pricePerPiece)} ea</LinePrice>
                    )}
                  </LineRight>
                </LineItemRow>
              );
            })}
          </Card>
        )}
      </Container>
    </Page>
  );
};

export default OrderTracker;
